import React from 'react'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { Box, Button, CircularProgress, Divider, InputBase, Link, Tooltip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'

import type { CouponTrayUIProps } from '@/src/types/couponTray'

const inputBoxSx = {
    display: 'flex',
    alignItems: 'center',
    border: '1px solid',
    borderColor: 'borders.default',
    borderRadius: '7.7px',
    px: 1.4,
    py: 0.5,
    width: '100%',
    bgcolor: 'background.paper',
    height: 39.3
} as const

const CouponTray: React.FC<
    CouponTrayUIProps & {
        isApplying?: boolean
        isMobile?: boolean
        onOpenCashbackModal?: () => void
        /** When true, tray is shown inside the view-all dialog (no page border; list scrolls with modal). */
        insideModal?: boolean
    }
> = ({
    inputField,
    typedInputField,
    offers,
    onInputChange,
    onApply,
    onRemove,
    onKnowMore,
    formatCurrency,
    isApplying,
    successMessages,
    appliedCouponCode,
    errorDetails,
    t,
    cashbackSchedule,
    isMobile,
    tCommon,
    onOpenCashbackModal,
    locale = 'en',
    showGvPinBlock,
    gvPinValue,
    onGvPinChange,
    onApplyGv,
    onRemoveGv,
    giftVoucherData,
    isApplyingGv,
    gvSuccessMessage,
    canApplyMoreGv = true,
    offerIndexSource,
    offersListMaxHeight,
    showViewAllCta,
    onViewAll,
    insideModal
}) => {
    // ✅ ONLY presentation — list slicing and layout limits live in `index.tsx`
    const indexSource = offerIndexSource ?? offers ?? []
    const displayedOffers = offers || []
    const showViewAll = !!showViewAllCta && !!onViewAll
    const listMaxHeight = insideModal ? undefined : offersListMaxHeight

    return (
        <Box
            sx={{
                bgcolor: 'neutralColors.white',
                borderRadius: '12px',
                border: !isMobile && !insideModal ? '1px solid' : 'none',
                borderColor: 'borders.default',
                p: 2,
                mb: insideModal ? 0 : 3.5,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                height: 'auto'
            }}
        >
            {/* Header Row */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.2, flexShrink: 0 }}>
                <Typography
                    textAlign={isMobile ? 'center' : 'left'}
                    fontWeight={700}
                    fontSize={isMobile ? 16 : 14.5}
                    flex={1}
                >
                    {t('applyCouponTit')}
                </Typography>
            </Box>

            {/* Single block: Coupon/Gift card input. Apply inside block only when !showGvPinBlock (Cleartrip). */}
            <Box mb={2} flexShrink={0}>
                <Box sx={inputBoxSx}>
                    <InputBase
                        placeholder={showGvPinBlock ? t('gvCodePlaceholder') : t('inputPlaceholder')}
                        value={inputField || typedInputField.value}
                        onChange={e => onInputChange(0, e.target.value)}
                        sx={{
                            flex: 1,
                            border: 'none',
                            outline: 'none',
                            bgcolor: 'transparent',
                            fontSize: 13,
                            fontWeight: 500,
                            color: 'text.secondary',
                            height: 30.9,
                            pl: 0,
                            pr: 1
                        }}
                        inputProps={{ style: { padding: 0, textTransform: 'uppercase' } }}
                        disabled={!!inputField}
                    />
                    {!!inputField ? (
                        <Button
                            type="button"
                            variant="text"
                            sx={{
                                ml: 1.4,
                                fontWeight: 700,
                                fontSize: 13,
                                bgcolor: 'transparent',
                                boxShadow: 'none',
                                border: 'none',
                                p: 0,
                                minWidth: 0,
                                color: 'text.secondary',
                                height: 30.9,
                                '&:hover': { bgcolor: 'transparent', color: 'error.main' }
                            }}
                            disableRipple
                            onClick={() => onRemove(0)}
                        >
                            {tCommon('remove', { defaultValue: 'Remove' })}
                        </Button>
                    ) : showGvPinBlock ? null : (
                        <Button
                            type="button"
                            variant="text"
                            sx={{
                                ml: 1.4,
                                fontWeight: 700,
                                fontSize: 13,
                                bgcolor: 'transparent',
                                boxShadow: 'none',
                                border: 'none',
                                p: 0,
                                minWidth: 0,
                                color: 'text.secondary',
                                height: 30.9,
                                '&:hover': { bgcolor: 'transparent', color: 'primary.main' }
                            }}
                            disableRipple
                            onClick={() => onApply({ code: typedInputField.value, source: 'inputBox', index: -1 })}
                            disabled={isApplying}
                        >
                            {isApplying ? <CircularProgress size={18} /> : tCommon('apply')}
                        </Button>
                    )}
                </Box>
                {typedInputField.error && (
                    <Typography color="error.main" fontSize={12} mt={1}>
                        {typedInputField.error}
                    </Typography>
                )}
                {errorDetails.error && errorDetails.index === 0 && errorDetails.msg && !showGvPinBlock && (
                    <Typography color="error.main" fontSize={12} mt={1}>
                        {errorDetails.msg}
                    </Typography>
                )}

                {/* Cleartrip: PIN input with Apply inside same box (like coupon/gift card input) */}
                {showGvPinBlock && onGvPinChange && onApplyGv && (
                    <Box sx={{ mt: 1.2, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                        <Box sx={inputBoxSx}>
                            <InputBase
                                placeholder={t('gvPinPlaceholder')}
                                value={gvPinValue ?? ''}
                                onChange={e => onGvPinChange(e.target.value)}
                                type="password"
                                sx={{
                                    flex: 1,
                                    border: 'none',
                                    outline: 'none',
                                    bgcolor: 'transparent',
                                    fontSize: 13,
                                    fontWeight: 500,
                                    color: 'text.primary',
                                    height: 30.9,
                                    pl: 0,
                                    pr: 1
                                }}
                                inputProps={{ style: { padding: 0 }, maxLength: 16 }}
                            />
                            <Button
                                type="button"
                                variant="text"
                                disabled={isApplyingGv || !canApplyMoreGv}
                                onClick={() =>
                                    canApplyMoreGv &&
                                    onApplyGv({
                                        code: typedInputField.value,
                                        pin: gvPinValue ?? ''
                                    })
                                }
                                sx={{
                                    ml: 1.4,
                                    fontWeight: 700,
                                    fontSize: 13,
                                    bgcolor: 'transparent',
                                    boxShadow: 'none',
                                    border: 'none',
                                    p: 0,
                                    minWidth: 0,
                                    color: 'text.secondary',
                                    height: 30.9,
                                    textTransform: 'none',
                                    '&:hover': { bgcolor: 'transparent', color: 'primary.main' }
                                }}
                                disableRipple
                            >
                                {isApplyingGv ? <CircularProgress size={18} /> : tCommon('apply', { defaultValue: 'Apply' })}
                            </Button>
                        </Box>
                        {!canApplyMoreGv && (
                            <Typography color="text.secondary" fontSize={12}>
                                {t('gvMaxReached')}
                            </Typography>
                        )}
                        {errorDetails.error && errorDetails.index === 0 && errorDetails.msg && (
                            <Typography color="error.main" fontSize={12}>
                                {errorDetails.msg}
                            </Typography>
                        )}
                        {giftVoucherData && giftVoucherData.length > 0 && onRemoveGv && (
                            <>
                                <Divider sx={{ my: 0.5 }} />
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {giftVoucherData.map((voucher, idx) => (
                                        <Box
                                            key={`${voucher.voucherCode}-${idx}`}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                py: 0.75
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 22 }} />
                                                <Typography fontWeight={700} fontSize={13} color="text.primary">
                                                    {voucher.voucherCode}
                                                </Typography>
                                            </Box>
                                            <Button
                                                type="button"
                                                variant="text"
                                                onClick={() => onRemoveGv(voucher)}
                                                sx={{
                                                    fontWeight: 700,
                                                    fontSize: 13,
                                                    color: 'primary.main',
                                                    textTransform: 'none',
                                                    p: 0,
                                                    minWidth: 0
                                                }}
                                                disableRipple
                                            >
                                                {tCommon('remove', { defaultValue: 'Remove' })}
                                            </Button>
                                        </Box>
                                    ))}
                                </Box>
                                {gvSuccessMessage && (
                                    <Typography color="success.main" fontSize={13} mt={0.5}>
                                        {gvSuccessMessage}
                                    </Typography>
                                )}
                            </>
                        )}
                    </Box>
                )}

                {/* Render all success messages */}
                {successMessages && successMessages.length > 0 && (
                    <Box mt={0.5}>
                        {successMessages.map((msg, i) => (
                            <Box key={i} mt={i === 0 ? 0 : 0.5}>
                                <Typography color="success.main" fontSize={13}>
                                    {msg}
                                </Typography>
                            </Box>
                        ))}
                        {cashbackSchedule != null &&
                            (isMobile ? (
                                <Button
                                    variant="text"
                                    onClick={onOpenCashbackModal}
                                    sx={{
                                        color: 'primary.main',
                                        fontWeight: 600,
                                        fontSize: 13,
                                        textTransform: 'none',
                                        mt: 1,
                                        p: 0,
                                        minWidth: 0
                                    }}
                                >
                                    {t('viewCashbackSchedule')}
                                </Button>
                            ) : (
                                <Tooltip
                                    title={
                                        <Box>
                                            <Typography fontWeight={600} fontSize={14} mb={1.2} sx={{ color: 'common.white' }}>
                                                {cashbackSchedule.message}
                                            </Typography>
                                            {cashbackSchedule.breakup &&
                                                cashbackSchedule.breakup.length > 0 &&
                                                cashbackSchedule.breakup.map((item, idx) => (
                                                    <Box
                                                        key={idx}
                                                        mb={idx !== cashbackSchedule.breakup.length - 1 ? 1 : 0}
                                                    >
                                                        <Typography fontSize={13} sx={{ color: 'common.white' }} mb={0.2}>
                                                            {item.date} {item.message ? `(${item.message})` : ''}
                                                        </Typography>
                                                        <Typography fontSize={13} sx={{ color: 'common.white' }}>
                                                            {t('amountWillBeAdded', {
                                                                amount: formatCurrency(item.amount)
                                                            })}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                        </Box>
                                    }
                                    placement="bottom"
                                    arrow
                                    slotProps={{
                                        tooltip: {
                                            sx: {
                                                bgcolor: 'grey.800',
                                                color: 'common.white',
                                                p: 2,
                                                maxWidth: 320,
                                                borderRadius: 2,
                                                fontSize: 12
                                            }
                                        }
                                    }}
                                >
                                    <Button
                                        variant="text"
                                        sx={{
                                            color: 'primary.main',
                                            fontWeight: 600,
                                            fontSize: 13,
                                            textTransform: 'none',
                                            mt: 1,
                                            p: 0,
                                            minWidth: 0
                                        }}
                                    >
                                        {t('viewCashbackSchedule')}
                                    </Button>
                                </Tooltip>
                            ))}
                    </Box>
                )}
            </Box>
            {/* Offers List (scrollable) */}
            <Box
                display="flex"
                flexDirection="column"
                gap={1.4}
                sx={{
                    flex: insideModal ? 'none' : 1,
                    overflowY: insideModal ? 'visible' : 'auto',
                    pr: 1,
                    minHeight: insideModal ? 'auto' : 0,
                    maxHeight: listMaxHeight,
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none'
                }}
            >
                {displayedOffers.map((offer, sliceIdx) => {
                    const fullIndex = indexSource.findIndex(o => o.id === offer.id)
                    const applyIndex = fullIndex >= 0 ? fullIndex : sliceIdx
                    const isApplied = offer.code === appliedCouponCode
                    const localizedDiscountText =
                        typeof offer.amount === 'number' && Number.isFinite(offer.amount)
                            ? t('flatAmountOff', { amount: formatCurrency(offer.amount) })
                            : ''
                    return (
                        <React.Fragment key={offer.id}>
                            <Box display="flex" flexDirection="column" gap={0.6} pb={1.4}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" pb={1}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.4,
                                            bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                                            borderRadius: '7.7px',
                                            px: 2.2,
                                            py: 0.7
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 19.5,
                                                height: 19.5,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            {typeof offer.icon === 'string' ? (
                                                <img
                                                    src={offer.icon}
                                                    alt={offer.code + ' logo'}
                                                    style={{ width: 19.5, height: 19.5, objectFit: 'contain' }}
                                                />
                                            ) : typeof offer.logo === 'string' ? (
                                                <img
                                                    src={offer.logo}
                                                    alt={offer.code + ' logo'}
                                                    style={{ width: 19.5, height: 19.5, objectFit: 'contain' }}
                                                />
                                            ) : (
                                                offer.icon || offer.logo
                                            )}
                                        </Box>
                                        <Typography fontWeight={700} color="text.primary" fontSize={13}>
                                            {offer.code}
                                        </Typography>
                                    </Box>
                                    <Button
                                        variant="text"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: 13,
                                            color: isApplied ? 'error.main' : 'primary.main',
                                            bgcolor: 'transparent',
                                            boxShadow: 'none',
                                            border: 'none',
                                            p: 0,
                                            minWidth: 0,
                                            height: 27.5,
                                            cursor: 'pointer',
                                            '&:hover': {
                                                bgcolor: 'transparent',
                                                color: isApplied ? 'error.main' : 'primary.main'
                                            }
                                        }}
                                        onClick={() =>
                                            isApplied
                                                ? onRemove(0)
                                                : onApply({
                                                      code: offer.code,
                                                      source: 'couponTray',
                                                      index: applyIndex
                                                  })
                                        }
                                        disableRipple
                                    >
                                        {isApplied ? tCommon('remove', { defaultValue: 'Remove' }) : tCommon('apply')}
                                    </Button>
                                </Box>
                                {localizedDiscountText ? (
                                    <Typography fontWeight={600} color="success.main" fontSize={13}>
                                        {localizedDiscountText}
                                    </Typography>
                                ): (
                                    <Typography fontWeight={700} color="success.main" fontSize={11}>
                                        {(locale === 'ar' ? offer.ar.heading : offer.en.heading) ||
                                            offer.en.heading}
                                    </Typography>
                                )}
                                <Typography fontWeight={500} color="text.secondary" fontSize={11} component="span">
                                    {(locale === 'ar' ? offer.ar.description : offer.en.description) ||
                                        offer.en.description}
                                    {offer.knowMoreLink && (
                                        <Link
                                            href={isMobile ? undefined : offer.knowMoreLink}
                                            target={isMobile ? undefined : '_blank'}
                                            rel={isMobile ? undefined : 'noopener noreferrer'}
                                            onClick={
                                                isMobile && onKnowMore
                                                    ? (e: React.MouseEvent) => {
                                                          e.preventDefault()
                                                          onKnowMore(offer.id)
                                                      }
                                                    : undefined
                                            }
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: 10.5,
                                                color: 'primary.main',
                                                ml: 0.5,
                                                textDecoration: 'none',
                                                cursor: 'pointer',
                                                verticalAlign: 'baseline'
                                            }}
                                        >
                                            {t('knowMore')}
                                        </Link>
                                    )}
                                </Typography>
                            </Box>
                            {sliceIdx !== displayedOffers.length - 1 && <Divider sx={{ my: 0.5 }} />}
                        </React.Fragment>
                    )
                })}
            </Box>
            {showViewAll && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', mt: 1 }}>
                    <Button
                        type="button"
                        variant="text"
                        onClick={onViewAll}
                        sx={{
                            p: 0,
                            minWidth: 0,
                            fontWeight: 600,
                            fontSize: 13,
                            color: 'primary.main',
                            textTransform: 'none',
                            '&:hover': { bgcolor: 'transparent', color: 'primary.main' }
                        }}
                        disableRipple
                    >
                        {t('viewAll')}
                    </Button>
                </Box>
            )}
        </Box>
    )
}

export default CouponTray
